import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Avatar,
    Paper,
    CircularProgress,
    Grid,
    IconButton,
    Tooltip,
} from "@mui/material";
import { AccountCircle, CloudUpload, CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";
import { PREDEFINED_AVATARS, getPredefinedAvatarImageUrl } from "../../utils/studentAvatarOptions";

const prefix = process.env.REACT_APP_STUDENT_PREFIX || "student";

const StudentProfile = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [petName, setPetName] = useState("");
    const [profileImage, setProfileImage] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [selectedAvatarKey, setSelectedAvatarKey] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploadPreview, setUploadPreview] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await api.get(`${prefix}/profile`);
                if (res.data?.success && res.data?.data) {
                    const d = res.data.data;
                    setPetName(d.pet_name || "");
                    setProfileImage(d.profile_image || null);
                    setProfileImageUrl(d.profile_image_url || null);
                    if (d.profile_image && String(d.profile_image).startsWith("avatar_")) {
                        setSelectedAvatarKey(d.profile_image);
                    } else {
                        setSelectedAvatarKey(null);
                    }
                }
            } catch (err) {
                toast.error("Failed to load profile");
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please select an image file (JPEG, PNG, GIF, WebP)");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Image must be under 2MB");
            return;
        }
        setUploadedFile(file);
        setSelectedAvatarKey(null);
        const reader = new FileReader();
        reader.onload = () => setUploadPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleSelectPredefined = (key) => {
        setSelectedAvatarKey(key);
        setUploadedFile(null);
        setUploadPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append("pet_name", petName.trim());

            if (uploadedFile) {
                formData.append("profile_image", uploadedFile);
            } else if (selectedAvatarKey) {
                formData.append("avatar_key", selectedAvatarKey);
            }

            const res = await api.post(`${prefix}/profile`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (res.data?.success) {
                const d = res.data.data;
                const stored = JSON.parse(localStorage.getItem("userData") || "{}");
                const updated = {
                    ...stored,
                    pet_name: d.pet_name ?? stored.pet_name,
                    profile_image: d.profile_image ?? stored.profile_image,
                    profile_image_url: d.profile_image_url ?? stored.profile_image_url,
                };
                localStorage.setItem("userData", JSON.stringify(updated));
                setProfileImage(d.profile_image ?? profileImage);
                setProfileImageUrl(d.profile_image_url ?? null);
                if (uploadedFile) {
                    setUploadedFile(null);
                    setUploadPreview(null);
                }
                toast.success("Profile saved! Your nick name will show on the leaderboard.");
            } else {
                toast.error(res.data?.message || "Failed to save profile");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save profile");
        } finally {
            setSaving(false);
        }
    };

    const currentDisplayUrl = uploadPreview || profileImageUrl || (selectedAvatarKey && !uploadedFile ? getPredefinedAvatarImageUrl(selectedAvatarKey) : null);
    const currentAvatarKey = !uploadPreview && !uploadedFile ? selectedAvatarKey : null;

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 560, mx: "auto", py: 3, px: 2 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: "#1e293b" }}>
                View & edit your profile
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose a profile picture and a nick name. Your nick name will be shown on the leaderboard instead of your real name.
            </Typography>

            <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: 3, mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    Profile picture
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3, flexWrap: "wrap" }}>
                    <Avatar
                        src={currentDisplayUrl}
                        sx={{
                            width: 80,
                            height: 80,
                            bgcolor: "grey.400",
                        }}
                    >
                        {!currentDisplayUrl && <AccountCircle sx={{ fontSize: 48 }} />}
                    </Avatar>
                    {/* Upload image - hidden, uncomment to show
                    <Box>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<CloudUpload />}
                            onClick={() => fileInputRef.current?.click()}
                            size="small"
                        >
                            Upload new image
                        </Button>
                    </Box>
                    */}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Pick an avatar
                </Typography>
                <Grid container spacing={1}>
                    {PREDEFINED_AVATARS.map((av) => (
                        <Grid item key={av.key}>
                            <Tooltip title="Select this avatar">
                                <IconButton
                                    onClick={() => handleSelectPredefined(av.key)}
                                    sx={{
                                        p: 0.5,
                                        border: selectedAvatarKey === av.key && !uploadedFile ? 3 : 1,
                                        borderColor: selectedAvatarKey === av.key && !uploadedFile ? "primary.main" : "divider",
                                        "&:hover": { borderColor: "primary.light" },
                                    }}
                                >
                                    <Box sx={{ position: "relative" }}>
                                        <Avatar
                                            src={av.imageUrl}
                                            sx={{ width: 36, height: 36 }}
                                        />
                                        {selectedAvatarKey === av.key && !uploadedFile && (
                                            <CheckCircle
                                                sx={{
                                                    position: "absolute",
                                                    bottom: -2,
                                                    right: -2,
                                                    fontSize: 18,
                                                    color: "primary.main",
                                                    bgcolor: "white",
                                                    borderRadius: "50%",
                                                }}
                                            />
                                        )}
                                    </Box>
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: 3, mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    Nick name (shown on leaderboard)
                </Typography>
                <TextField
                    fullWidth
                    placeholder="e.g. SuperStar, MathWiz"
                    value={petName}
                    onChange={(e) => setPetName(e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    helperText={`${petName.length}/100 characters`}
                />
            </Paper>

            <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ px: 4, py: 1.5 }}
            >
                {saving ? "Saving..." : "Save profile"}
            </Button>
        </Box>
    );
};

export default StudentProfile;
